package com.eladfinish.bible.contest.screens.pairMatching.ui

import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.tween
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.pair_matching_progress_format
import com.eladfinish.bible.contest.ui.common.PreviewTheme
import dev.icerock.moko.resources.compose.stringResource

private val progressGreen = Color(0xFF1B5E20)

/**
 * Top-of-screen progress indicator for the PairMatching game.
 *
 * Shows "X/Y pairs matched" and a horizontal bar that animates to the completion fraction.
 *
 * @param matched number of correctly matched pairs so far.
 * @param total   total number of pairs in the current topic.
 */
@Composable
fun PairMatchingProgress(
    matched: Int,
    total: Int,
    modifier: Modifier = Modifier,
) {
    val targetFraction = if (total > 0) matched.toFloat() / total else 0f
    val animatedFraction by animateFloatAsState(
        targetValue = targetFraction,
        animationSpec = tween(durationMillis = 400),
        label = "pairMatchingProgress",
    )

    Column(
        modifier = modifier
            .fillMaxWidth()
            .padding(horizontal = 16.dp, vertical = 8.dp),
    ) {
        Text(
            text = stringResource(SharedRes.strings.pair_matching_progress_format, matched, total),
            modifier = Modifier.fillMaxWidth(),
            textAlign = TextAlign.Center,
            fontSize = 16.sp,
            style = MaterialTheme.typography.titleSmall,
            color = Color.Black,
        )

        LinearProgressIndicator(
            progress = { animatedFraction },
            modifier = Modifier
                .fillMaxWidth()
                .padding(top = 6.dp)
                .clip(RoundedCornerShape(50)),
            color = progressGreen,
            trackColor = Color.Black.copy(alpha = 0.12f),
        )
    }
}

// ─── Preview ──────────────────────────────────────────────────────────────────

@Preview(showBackground = true, name = "Progress 4/8")
@Composable
private fun PairMatchingProgressPreview() {
    PreviewTheme {
        PairMatchingProgress(matched = 4, total = 8)
    }
}

@Preview(showBackground = true, name = "Progress 0/8")
@Composable
private fun PairMatchingProgressEmptyPreview() {
    PreviewTheme {
        PairMatchingProgress(matched = 0, total = 8)
    }
}

@Preview(showBackground = true, name = "Progress 8/8")
@Composable
private fun PairMatchingProgressFullPreview() {
    PreviewTheme {
        PairMatchingProgress(matched = 8, total = 8)
    }
}
